const React = require('react')
const { useState, useEffect } = require('react')
const { useHistory, useLocation } = require('react-router-dom')
const { Container, Col, Row, Table, UncontrolledTooltip } = require('reactstrap')
const { useBlockchainData, useFormatTimestampShort, useFormatDateTime, useResponsive, BREAKPOINTS } = require('@hooks')
const { useNetworkConfig } = require('@contexts')
const { LoadingSpinner } = require('@components')
const { TableHeader, TableFooter } = require('@components/table')

require('@styles/pages/blocks/index.css')

const Epochs = () => {
    const location = useLocation()
    const history = useHistory()
    const { network } = useNetworkConfig()
    const { getEpochs } = useBlockchainData(network.name)
    const formatTimestamp = useFormatTimestampShort()
    const formatDateTime = useFormatDateTime()

    const params = new URLSearchParams(location.search)

    const [page, setPage] = useState(Number(params.get('p')) || 1)
    const [limit, setLimit] = useState(Number(params.get('ps')) || 25)
    const [epochsData, setEpochsData] = useState()
    const [showAge, setShowAge] = useState(true)

    const isMobile = useResponsive(BREAKPOINTS.medium)

    const [loading, setLoading] = useState(false)

    useEffect(() => {
        const params = new URLSearchParams(location.search)
        const pageParam = Number(params.get('p'))
        const limitParam = Number(params.get('ps'))

        if (pageParam && pageParam > 0 && pageParam !== page) {
            setPage(pageParam)
        }

        if (limitParam && limitParam > 0 && limitParam !== limit) {
            setLimit(limitParam)
        }
    }, [location.search])

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true)

            try {
                const [epochsResponse] = await Promise.all([getEpochs(page, limit)])

                if (epochsResponse) {
                    setEpochsData(epochsResponse)
                }
            } catch (error) {
                console.error('Error fetching data:', error)
            } finally {
                setLoading(false)
            }
        }

        fetchData()
    }, [network, page, limit])

    return (
        <div className='page'>
            <Container>
                <Col>
                    <Row>
                        <div className='page-header'>
                            <text className='page-title'>Epochs</text>
                            <div className='page-title-border' />
                        </div>
                    </Row>
                    <div className='box table-section' style={{ padding: '24px 24px 0' }}>
                        {loading ? (
                            <div
                                className='d-flex justify-content-center align-items-center'
                                style={{ height: '300px' }}
                            >
                                <LoadingSpinner />
                            </div>
                        ) : (
                            <>
                                <TableHeader
                                    title={`Total of ${epochsData?.totalItems.toLocaleString()} epochs`}
                                    page={page}
                                    limit={limit}
                                    totalPages={epochsData?.totalPages}
                                />
                                <Table
                                    responsive
                                    style={
                                        isMobile ? { display: 'block', overflowX: 'auto', whiteSpace: 'nowrap' } : {}
                                    }
                                >
                                    <thead>
                                        <tr>
                                            <th>Epoch</th>
                                            <th>
                                                <span
                                                    id='age-tooltip'
                                                    className={`${network.name}-text-color cursor-pointer`}
                                                    onClick={() => setShowAge(!showAge)}
                                                >
                                                    {showAge ? 'Age' : 'Date Time (UTC)'}
                                                </span>
                                                <UncontrolledTooltip placement='top' target='age-tooltip'>
                                                    {showAge
                                                        ? 'Click to show Datetime Format'
                                                        : 'Click to show Age Format'}
                                                </UncontrolledTooltip>
                                            </th>
                                            <th>Proposed</th>
                                            <th>Skip</th>
                                            <th>Fork</th>
                                            <th>Att</th>
                                            <th>Dep</th>
                                            <th>S-P/A</th>
                                            <th>Eligible ETH</th>
                                            <th>Voted ETH</th>
                                            <th>Final</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {epochsData?.items.map(epoch => (
                                            <tr key={epoch.epoch}>
                                                <td>
                                                    <span
                                                        className={`${network.name}-text-color cursor-pointer`}
                                                        onClick={() =>
                                                            history.push(`/${network.name}/epoch/${epoch.epoch}`)
                                                        }
                                                    >
                                                        {epoch.epoch}
                                                    </span>
                                                </td>
                                                <td>
                                                    {showAge ? (
                                                        <>
                                                            <span id={'age-' + epoch.epoch}>
                                                                {formatTimestamp(epoch.timestamp)}
                                                            </span>
                                                            <UncontrolledTooltip
                                                                placement='top'
                                                                target={`age-${epoch.epoch}`}
                                                            >
                                                                {formatDateTime(epoch.timestamp)}
                                                            </UncontrolledTooltip>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <span id={'datetime-' + epoch.epoch}>
                                                                {formatDateTime(epoch.timestamp)}
                                                            </span>
                                                            <UncontrolledTooltip
                                                                placement='top'
                                                                target={`datetime-${epoch.epoch}`}
                                                            >
                                                                {formatTimestamp(epoch.timestamp)}
                                                            </UncontrolledTooltip>
                                                        </>
                                                    )}
                                                </td>
                                                <td>{32 - epoch.skippedSlotsCount - epoch.forkedSlotsCount}</td>
                                                <td>{epoch.skippedSlotsCount}</td>
                                                <td>{epoch.forkedSlotsCount}</td>
                                                <td>{epoch.attestationsCount}</td>
                                                <td>{epoch.depositsCount}</td>
                                                <td>
                                                    {epoch.proposerSlashingsCount} / {epoch.attesterSlashingsCount}
                                                </td>
                                                <td>
                                                    {(epoch.eligibleBalance / 10 ** 9).toLocaleString(undefined, {
                                                        minimumFractionDigits: 0,
                                                        maximumFractionDigits: 9
                                                    })}
                                                    {network.data.token}
                                                </td>
                                                <td>
                                                    {(epoch.votedBalance / 10 ** 9).toLocaleString(undefined, {
                                                        minimumFractionDigits: 0,
                                                        maximumFractionDigits: 9
                                                    })}
                                                    &nbsp;{network.data.token}&nbsp;
                                                    <span
                                                        id={`percent-gas-used-${epoch.epoch}`}
                                                        style={{ color: 'var(--grey-color)', fontSize: '12px' }}
                                                    >
                                                        (
                                                        {(
                                                            (epoch.votedBalance / epoch.eligibleBalance) * 100 || 0
                                                        ).toFixed(2)}
                                                        %)
                                                    </span>
                                                </td>
                                                <td>
                                                    {epoch.isFinalized ? (
                                                        <span style={{ color: 'var(--success-color)' }}>Yes</span>
                                                    ) : (
                                                        <span style={{ color: 'var(--warning-color)' }}>No</span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                                <TableFooter
                                    limit={limit}
                                    setLimit={setLimit}
                                    page={page}
                                    setPage={setPage}
                                    totalPages={epochsData?.totalPages}
                                />
                            </>
                        )}
                    </div>
                </Col>
            </Container>
        </div>
    )
}

module.exports = Epochs
